import { Carousel } from 'bootstrap'

// Gap between slides — must match CSS `column-gap: 1rem` (16px at default font-size)
const SLIDE_GAP = 16

// Auto-incrementing ID for single-item carousels that lack an explicit id attribute
let carouselIdCounter = 0

/**
 * Builds the external controls bar (prev button, dot indicators, next button)
 * and inserts it immediately after `anchor` in the DOM.
 *
 * @param {Element}  anchor  - Element after which the controls are inserted
 * @param {number}   count   - Number of real slides (determines dot count)
 * @param {Function} onPrev  - Called when prev button is clicked
 * @param {Function} onNext  - Called when next button is clicked
 * @param {Function} onDot   - Called with slide index when a dot is clicked
 * @returns {HTMLUListElement} The dots list element (used for syncDots)
 */
const buildControls = (anchor, count, onPrev, onNext, onDot) => {
    const controls = document.createElement('div')
    controls.className = 'carousel-controls'

    const prevBtn = document.createElement('button')
    prevBtn.type = 'button'
    prevBtn.className = 'carousel-btn carousel-btn--prev'
    prevBtn.setAttribute('aria-label', 'Předchozí')
    prevBtn.addEventListener('click', onPrev)

    const dotsList = document.createElement('ul')
    dotsList.className = 'carousel-dots'
    dotsList.setAttribute('role', 'tablist')

    for (let i = 0; i < count; i++) {
        const li = document.createElement('li')
        if (i === 0) li.classList.add('active')

        const btn = document.createElement('button')
        btn.type = 'button'
        btn.setAttribute('role', 'tab')
        btn.setAttribute('aria-label', String(i + 1))
        btn.setAttribute('tabindex', i === 0 ? '0' : '-1')
        btn.addEventListener('click', () => onDot(i))

        li.appendChild(btn)
        dotsList.appendChild(li)
    }

    const nextBtn = document.createElement('button')
    nextBtn.type = 'button'
    nextBtn.className = 'carousel-btn carousel-btn--next'
    nextBtn.setAttribute('aria-label', 'Následující')
    nextBtn.addEventListener('click', onNext)

    controls.append(prevBtn, dotsList, nextBtn)
    anchor.after(controls)
    return dotsList
}

/**
 * Marks the dot at `index` as active and updates tabindex for keyboard access.
 *
 * @param {HTMLUListElement} dotsList
 * @param {number}           index    - Zero-based index of the active slide
 */
const syncDots = (dotsList, index) => {
    [...dotsList.children].forEach((li, i) => {
        li.classList.toggle('active', i === index)
        li.querySelector('button').setAttribute('tabindex', i === index ? '0' : '-1')
    })
}

// ── Single-item carousel (.c-carousel--single) ──────────────────────────────────────
// Delegates fully to Bootstrap Carousel: touch/swipe, keyboard, animation,
// wrap-around. JS only restructures the DOM and wires up external controls.

/**
 * Initialises a Bootstrap Carousel on `el` for single-item display.
 * Skips if there is only one child (no navigation needed).
 *
 * @param {Element} el - The `.c-carousel--single` container element
 */
const initSingleCarousel = (el) => {
    const items = [...el.children]
    if (items.length <= 1) return

    if (!el.id) el.id = `carousel-nic-${++carouselIdCounter}`
    el.classList.add('carousel', 'slide')

    // Wrap every child in a Bootstrap `.carousel-item` div
    const inner = document.createElement('div')
    inner.className = 'carousel-inner'

    items.forEach((item, i) => {
        const wrapper = document.createElement('div')
        wrapper.className = `carousel-item${i === 0 ? ' active' : ''}`
        wrapper.appendChild(item)
        inner.appendChild(wrapper)
    })
    el.appendChild(inner)

    // Lock carousel-inner to the tallest slide so height never jumps during navigation.
    // Non-active items are display:none — briefly force display:flex to measure them.
    const wrappers = [...inner.querySelectorAll('.carousel-item')]
    const lockHeight = () => {
        const hidden = wrappers.filter((w) => !w.classList.contains('active'))
        hidden.forEach((w) => { w.style.display = 'flex' })
        inner.style.removeProperty('height')  // clear first so height:100% on items resolves to auto
        const buffer = window.innerWidth < 768 ? 32 : 0
        inner.style.height = `${Math.max(...wrappers.map((w) => w.offsetHeight)) + buffer}px`
        hidden.forEach((w) => { w.style.removeProperty('display') })
    }
    lockHeight()

    let resizeTimer
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(lockHeight, 100)
    })

    const instance = new Carousel(el, { wrap: true, keyboard: true, touch: true, interval: false })

    const dotsList = buildControls(
        el,
        items.length,
        () => instance.prev(),
        () => instance.next(),
        (i) => instance.to(i),
    )

    // Keep dots in sync with Bootstrap's own slide event
    el.addEventListener('slide.bs.carousel', (e) => syncDots(dotsList, e.to))
    el.classList.add('carousel-initialized')
}

// ── Multi-item carousel (.c-carousel) ──────────────────────────────────────
// Bootstrap Carousel does not support multi-item natively, so we implement a
// custom infinite-loop track using pixel maths, DOM cloning, and CSS transform.

/**
 * Returns the number of slides visible at the current viewport width.
 * Breakpoints mirror the Bootstrap grid: lg ≥ 992 px, sm ≥ 576 px.
 *
 * @returns {1|2|3}
 */
const getItemsToShow = () => {
    if (window.innerWidth >= 992) return 3
    if (window.innerWidth >= 576) return 2
    return 1
}

/**
 * Initialises a custom multi-item infinite carousel on `el`.
 * DOM after init:
 *   .c-carousel
 *     .carousel-viewport   (overflow: hidden)
 *       .carousel-track    (CSS transform drives movement)
 *         [start clones] [real slides] [end clones]
 *   .carousel-controls     (inserted after .c-carousel)
 *
 * @param {Element} el - The `.c-carousel` container element
 */
const initMultiCarousel = (el) => {
    const origItems = [...el.children]
    const count = origItems.length
    if (count === 0) return

    // Build viewport + track wrapper
    const viewport = document.createElement('div')
    viewport.className = 'carousel-viewport'
    // Inline styles ensure clipping works regardless of CSS load order
    viewport.style.overflow = 'hidden'
    viewport.style.width = '100%'

    const track = document.createElement('div')
    track.className = 'carousel-track'

    origItems.forEach((item) => {
        item.classList.add('carousel-slide')
        track.appendChild(item)
    })
    viewport.appendChild(track)
    el.appendChild(viewport)

    // No navigation needed for a single slide
    if (count <= 1) return

    let currentIndex = 0
    let itemsToShow = Math.min(getItemsToShow(), count)
    let cloneCount = 0   // number of clones prepended (= itemsToShow)
    let slideWidth = 0   // computed pixel width of one slide
    let isAnimating = false

    /** Returns all track children including clones. */
    const allSlides = () => [...track.children]

    /**
     * Rebuilds the clone nodes that enable seamless looping.
     * Prepends clones of the last `itemsToShow` real slides (reversed so the
     * visual order is correct) and appends clones of the first `itemsToShow`.
     */
    const rebuildClones = () => {
        // Remove existing clones before rebuilding
        allSlides().filter((s) => s.dataset.clone).forEach((c) => c.remove())

        const real = [...track.children]
        cloneCount = itemsToShow

        // Prepend: iterate real slides backwards from the end
        for (let i = real.length - 1; i >= real.length - itemsToShow; i--) {
            const clone = real[i].cloneNode(true)
            clone.dataset.clone = 'start'
            clone.setAttribute('aria-hidden', 'true')
            track.insertBefore(clone, track.firstChild)
        }

        // Append: clone the first N slides
        for (let i = 0; i < itemsToShow; i++) {
            const clone = real[i].cloneNode(true)
            clone.dataset.clone = 'end'
            clone.setAttribute('aria-hidden', 'true')
            track.appendChild(clone)
        }
    }

    /**
     * Recalculates slide widths and track width based on the current
     * viewport pixel size.  Uses integer-free pixel maths — no percentages
     * or getComputedStyle (which can return "normal" for column-gap).
     */
    const updateLayout = () => {
        const all = allSlides()
        slideWidth = (viewport.offsetWidth - (itemsToShow - 1) * SLIDE_GAP) / itemsToShow
        const step = slideWidth + SLIDE_GAP
        track.style.width = `${step * all.length - SLIDE_GAP}px`
        all.forEach((slide) => {
            slide.style.flex = `0 0 ${slideWidth}px`
            slide.style.minWidth = '0'
        })
    }

    /**
     * Moves the track to the position corresponding to `currentIndex`.
     *
     * @param {boolean} animated - Whether to play the CSS transition
     */
    const setPosition = (animated) => {
        const offset = -(currentIndex + cloneCount) * (slideWidth + SLIDE_GAP)
        track.style.transition = animated ? 'transform 0.4s ease' : 'none'
        track.style.transform = `translateX(${offset}px)`
    }

    /**
     * Maps any currentIndex (including negative or beyond count) to a
     * valid dot index using modular arithmetic.
     *
     * @returns {number}
     */
    const activeDot = () => ((currentIndex % count) + count) % count

    let dotsList

    /**
     * Advances the carousel by `dir` (+1 forward, -1 backward).
     * Guards against double-firing during an ongoing transition.
     *
     * @param {1|-1} dir
     */
    const navigate = (dir) => {
        if (isAnimating) return
        isAnimating = true
        currentIndex += dir
        setPosition(true)
        syncDots(dotsList, activeDot())
    }

    /**
     * Jumps directly to slide `i` (dot click).
     *
     * @param {number} i - Zero-based real slide index
     */
    const navigateTo = (i) => {
        if (isAnimating) return
        currentIndex = i
        setPosition(true)
        syncDots(dotsList, i)
    }

    dotsList = buildControls(el, count, () => navigate(-1), () => navigate(1), navigateTo)

    // After each CSS transition, release the animation lock and jump from a
    // clone position back to the corresponding real slide (infinite loop seam).
    track.addEventListener('transitionend', () => {
        isAnimating = false
        if (currentIndex < 0) {
            currentIndex = count - 1
            setPosition(false)
        } else if (currentIndex >= count) {
            currentIndex = 0
            setPosition(false)
        }
        syncDots(dotsList, activeDot())
    })

    // Touch / swipe — 40 px minimum drag to trigger navigation
    let touchStartX = 0
    viewport.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX
    }, { passive: true })
    viewport.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].clientX
        if (Math.abs(diff) > 40) navigate(diff > 0 ? 1 : -1)
    }, { passive: true })

    // Debounced resize handler: rebuild clones when the breakpoint changes,
    // then recalculate pixel widths and snap to position without animation.
    let resizeTimer
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(() => {
            const n = Math.min(getItemsToShow(), count)
            if (n !== itemsToShow) {
                itemsToShow = n
                rebuildClones()
            }
            updateLayout()
            setPosition(false)
        }, 100)
    })

    rebuildClones()

    // Use ResizeObserver rather than DOMContentLoaded or rAF so that layout is
    // computed only once the viewport element has real pixel dimensions — even
    // if the element starts hidden or is rendered late (e.g. inside a tab).
    const ro = new ResizeObserver(() => {
        if (viewport.offsetWidth > 0) {
            ro.disconnect()
            updateLayout()
            setPosition(false)
            syncDots(dotsList, 0)
            el.classList.add('carousel-initialized')
        }
    })
    ro.observe(viewport)
}

/**
 * Entry point — call once after DOMContentLoaded.
 * Initialises all `.c-carousel` (multi-item) and `.c-carousel--single` (single-item)
 * elements found in the document.
 */
export const initCarousels = () => {
    document.querySelectorAll('.c-carousel').forEach(initMultiCarousel)
    document.querySelectorAll('.c-carousel--single').forEach(initSingleCarousel)
}
